/**
 * Converts a lowercase Provider key to its display-cased CloudProvider form.
 *
 * Use for a typed `CloudProvider!` GraphQL argument, a `ProviderIcon` prop, or
 * human-readable text. Do NOT use for a filter value, a relay `@connection`
 * cache key, or anything else compared against the raw key -- pass `provider`
 * directly there. For every provider but Kubernetes this only changes case,
 * so a case-insensitive comparison downstream can mask the misuse; "k8s" ->
 * "Kubernetes" is a different word, so that misuse breaks outright.
 *
 * @param provider - The lowercase Provider enum value to convert.
 * @returns The corresponding uppercase CloudProvider enum value.
 *
 * @example
 * ```typescript
 * formatProvider("aws") // Returns "AWS"
 * formatProvider("gcp") // Returns "GCP"
 * formatProvider("k8s") // Returns "Kubernetes"
 * formatProvider("unknown") // Returns "AWS" (default fallback)
 * ```
 */

export type CloudProvider =
  | "AWS"
  | "Azure"
  | "GCP"
  | "TencentCloud"
  | "Kubernetes"
  | "%future added value";

export const DEFAULT_PROVIDERS = ["aws", "azure", "gcp", "tencentcloud", "k8s"] as const;

export type Provider = (typeof DEFAULT_PROVIDERS)[number] | "%future added value";

// Typeguard to check if a value is a valid Provider
export function isProvider(value: string | Provider): value is Provider {
  return typeof value === "string" && (DEFAULT_PROVIDERS as readonly string[]).includes(value);
}

export function formatProvider(provider: Provider): CloudProvider {
  switch (provider) {
    case "aws":
      return "AWS";
    case "azure":
      return "Azure";
    case "gcp":
      return "GCP";
    case "tencentcloud":
      return "TencentCloud";
    case "k8s":
      return "Kubernetes";
    default:
      return "AWS";
  }
}

export type CloudProviderFullName =
  | "Amazon Web Services"
  | "Microsoft Azure"
  | "Google Cloud Platform"
  | "Tencent Cloud"
  | "Kubernetes"
  | "%future added value";

export function formatProviderFullName(provider: Provider): CloudProviderFullName {
  switch (provider) {
    case "aws":
      return "Amazon Web Services";
    case "azure":
      return "Microsoft Azure";
    case "gcp":
      return "Google Cloud Platform";
    case "tencentcloud":
      return "Tencent Cloud";
    case "k8s":
      return "Kubernetes";
    default:
      return "Amazon Web Services";
  }
}
